// v1.2.0 — the live NoteSource: MNote v2.2 (series A) and, v0.5, MNoteCents v2.3 (series B, cents) on Arc.
//
// READS go straight to Arc's public RPC from the browser (viem's own list for
// the chain — primary, QuickNode, Blockdaemon — in fallback order; all three
// answer mdrop.it's origin, checked 2 Oct 2026). Not through Circle's modular
// transport and not through M's Worker: a note must be checkable with no
// wallet, no login and no M key — the Take page is opened by people who have
// none of them. One read answers a serial (noteBySeq); one read lists a
// holder's notes (notesOfWithState).
//
// WRITES are not made here. Every method returns calls ({ to, data }) for the
// wallet adapter the screens already use: adapter.prepare(calls) while the
// card is shown, adapter.sendPrepared on the slide, adapter.submit as the
// plain path. So notes inherit prepare-before-the-slide, the paymaster, the
// gas floor and success-at-acceptance with nothing new in the adapter.
//
// The contract a note lives in is named by its serial's series letter
// (serial.ts SERIES), so a note of any known series can be read and taken.
// ISSUING needs VITE_NOTES_ADDRESS (the Pages build var) to name one of those
// contracts — that is the switch that turns M® Note on in a build.

import {
  createPublicClient, encodeFunctionData, fallback, http, type Hex,
} from "viem";
import { privateKeyToAccount } from "viem/accounts";
import { arcTestnet } from "../chains";
import {
  ERC20_APPROVE_ABI, ERC20_TRANSFER_ABI, MNOTE_ABI, NOTE_DOMAIN_NAME, NOTE_DOMAIN_VERSION, TRANSFER_NOTE_TYPES,
} from "./abi";
import { makeSerial, SERIES, type Serial, type Series } from "./serial";
import type { Call, NoteSource, NoteStatus, NoteView } from "./notes";

export class NotesUnavailableError extends Error {
  constructor(why: string) {
    super(why);
    this.name = "NotesUnavailableError";
  }
}

/** One eth_call against a note contract. Injectable, so tests need no network. */
export type NoteReader = (q: {
  address: Hex;
  functionName: "noteBySeq" | "notesOfWithState";
  args: readonly unknown[];
}) => Promise<unknown>;

interface RawNote { value: bigint; controller: Hex; version: bigint; seq: bigint; status: number; salt: Hex }

const ZERO_ID = `0x${"00".repeat(32)}`;
const ZERO_SALT_HEX = `0x${"00".repeat(32)}`;
/** 1.4.68 — USDC on Arc: the gas token, the same address every series' dollars sit in (SERIES.B.token). */
export const GAS_TOKEN = "0x3600000000000000000000000000000000000000";
/** 1.4.71 — the M® Note fee when the Worker doesn't say (an older or unreachable Worker): $0.02. Cashing in costs well under a cent. */
export const NOTE_FEE_DEFAULT = 0.02;
/** Dollars → USDC's units (6 dp), to the cent. */
export const feeUnits = (usd: number): bigint => BigInt(Math.round(usd * 100)) * 10_000n;
/** 1.4.71 — the fee's calls: `units` of USDC to the key, after `convert` (approve + MFx.swap) when it comes from another currency. */
export interface NoteFeeCalls { units: bigint; convert?: Call[] }
const STATUS: Record<number, NoteStatus | undefined> = { 1: "live", 2: "spent", 3: "redeemed" };
/** How long a TransferNote signature stays valid. It can only move the note to the taker; this is tidiness. */
export const TAKE_DEADLINE_S = 600;

const same = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();

function publicReader(): NoteReader {
  const urls = (arcTestnet.rpcUrls.default.http as readonly string[]).map((u) => http(u, { timeout: 8_000 }));
  const client = createPublicClient({ chain: arcTestnet, transport: fallback(urls) });
  return (q) => client.readContract({ address: q.address, abi: MNOTE_ABI, functionName: q.functionName, args: q.args } as never);
}

function configuredAddress(): string | undefined {
  // Vite inlines import.meta.env at build; under Node (tests) it isn't there.
  const env = (import.meta as { env?: Record<string, string | undefined> }).env;
  return env?.VITE_NOTES_ADDRESS || undefined;
}

export class ArcNoteSource implements NoteSource {
  private reader: NoteReader | null;
  private readonly issuing: { letter: string; series: Series } | null;
  /** 1.4.55 — the issuing series per currency: the named one for its currency, then every series marked `issue`. */
  private readonly byCurrency: Record<string, Series>;

  /**
   * `address` — the contract new notes are issued on (default: VITE_NOTES_ADDRESS).
   * `read` — how to read Arc (default: the public RPC).
   */
  constructor(opts: { address?: string; read?: NoteReader } = {}) {
    this.reader = opts.read ?? null;
    const address = opts.address ?? configuredAddress();
    const hit = address ? Object.entries(SERIES).find(([, s]) => same(s.contract, address)) : undefined;
    this.issuing = hit ? { letter: hit[0], series: hit[1] } : null;
    this.byCurrency = {};
    if (this.issuing) {
      this.byCurrency[this.issuing.series.currency] = this.issuing.series;
      for (const s of Object.values(SERIES)) if (s.issue && !this.byCurrency[s.currency]) this.byCurrency[s.currency] = s;
    }
  }

  issuingCurrencies(): string[] {
    return Object.keys(this.byCurrency);
  }

  get live(): boolean {
    return this.issuing !== null;
  }

  private read(q: Parameters<NoteReader>[0]): Promise<unknown> {
    return (this.reader ??= publicReader())(q);
  }

  private view(letter: string, id: Hex, n: RawNote): NoteView | null {
    const status = STATUS[Number(n.status)];
    if (!status || id === ZERO_ID) return null;
    return {
      id,
      serial: makeSerial(letter, n.seq),
      value: dollarsOf(n.value),
      units: BigInt(n.value),
      currency: SERIES[letter].currency,
      controller: n.controller,
      version: BigInt(n.version),
      status,
      salt: n.salt,
    };
  }

  private seriesOf(note: NoteView): Series {
    const s = SERIES[note.serial.series];
    if (!s) throw new NotesUnavailableError(`unknown series ${note.serial.series}`);
    return s;
  }

  async bySerial(serial: Serial): Promise<NoteView | null> {
    const s = SERIES[serial.series];
    if (!s || s.chainId !== arcTestnet.id) return null; // a series this build doesn't know
    const [id, n] = (await this.read({ address: s.contract as Hex, functionName: "noteBySeq", args: [serial.seq] })) as [Hex, RawNote];
    const v = this.view(serial.series, id, n);
    return v && v.serial.seq === serial.seq ? v : null;
  }

  async heldBy(address: string): Promise<NoteView[]> {
    const out: NoteView[] = [];
    for (const [letter, s] of Object.entries(SERIES)) {
      if (s.chainId !== arcTestnet.id || !/^0x[0-9a-fA-F]{40}$/.test(s.contract)) continue; // 1.4.55: a series whose address isn't in yet
      const [ids, notes] = (await this.read({ address: s.contract as Hex, functionName: "notesOfWithState", args: [address] })) as [readonly Hex[], readonly RawNote[]];
      ids.forEach((id, i) => {
        const v = this.view(letter, id, notes[i]);
        if (v) out.push(v);
      });
    }
    return out;
  }

  issueCalls(amount: number, controller: string, salt: Hex, currency?: string, opts: { fee?: NoteFeeCalls } = {}): Call[] {
    if (!this.issuing) throw new NotesUnavailableError("M® Notes are not switched on in this build (VITE_NOTES_ADDRESS).");
    const series = this.byCurrency[currency ?? this.issuing.series.currency];
    if (!series) throw new NotesUnavailableError(`No note series holds ${currency} yet.`);
    const units = toUnits(amount, series);
    const calls: Call[] = [];
    // 1.4.68 (Alexander, 21 Oct) — the note carries its own gas, so the key the code stands for can cash the note in by
    // itself, straight to the taker's wallet address, with no Face ID of the taker's. 1.4.71 — it is the M® Note fee:
    // `fee.units` of USDC (Arc's gas, whatever the note's currency) to the key's address, in the same userOp; for a note
    // in another currency `fee.convert` comes first (approve + MFx.swap, the note's currency into those dollars —
    // the fee pro rata). Only for a bearer note: a wallet-held note has no key to pay.
    if (opts.fee && opts.fee.units > 0n && salt !== ZERO_SALT_HEX) {
      calls.push(...(opts.fee.convert ?? []), {
        to: GAS_TOKEN,
        data: encodeFunctionData({ abi: ERC20_TRANSFER_ABI, functionName: "transfer", args: [controller as Hex, opts.fee.units] }),
      });
    }
    calls.push(
      {
        to: series.token,
        data: encodeFunctionData({ abi: ERC20_APPROVE_ABI, functionName: "approve", args: [series.contract as Hex, units] }),
      },
      {
        to: series.contract,
        data: encodeFunctionData({ abi: MNOTE_ABI, functionName: "issueNote", args: [controller as Hex, units, salt] }),
      },
    );
    return calls;
  }

  async takeCalls(note: NoteView, key: Hex, taker: string, opts: { keep?: boolean; amount?: number; now?: number } = {}): Promise<Call[]> {
    const series = this.seriesOf(note);
    const signer = privateKeyToAccount(key);
    // Check before spending: a key that isn't the controller's would only buy a revert.
    if (!same(signer.address, note.controller)) throw new Error("This secret doesn't open this note.");
    const units = opts.amount === undefined ? note.units : toUnits(opts.amount, series, note);
    // v0.4 — a take ends in dollars. Part of a note comes out as a NEW note whose id
    // isn't known until Arc mints it, so it can't be cashed in the same userOp.
    if (!opts.keep && units !== note.units) throw new Error("Part of a note can only be taken as a note (keep: true).");
    const deadline = BigInt(Math.floor((opts.now ?? Date.now()) / 1000) + TAKE_DEADLINE_S);
    const sig = await signer.signTypedData({
      domain: { name: NOTE_DOMAIN_NAME, version: NOTE_DOMAIN_VERSION, chainId: series.chainId, verifyingContract: series.contract as Hex },
      types: TRANSFER_NOTE_TYPES,
      primaryType: "TransferNote",
      message: { noteId: note.id, version: note.version, newController: taker as Hex, amount: units, deadline },
    });
    const calls: Call[] = [
      {
        to: series.contract,
        data: encodeFunctionData({
          abi: MNOTE_ABI,
          functionName: "transferNoteBySig",
          args: [note.id, taker as Hex, note.version, units, deadline, sig],
        }),
      },
    ];
    if (!opts.keep) {
      // The whole note is now the taker's, one version on; the taker's wallet cashes
      // it in to itself in the same userOp. Both calls succeed or neither does.
      calls.push({
        to: series.contract,
        data: encodeFunctionData({
          abi: MNOTE_ABI,
          functionName: "redeemNote",
          args: [note.id, taker as Hex, note.version + 1n, units],
        }),
      });
    }
    return calls;
  }

  handOverCalls(note: NoteView, to: string, amount?: number): Call[] {
    const series = this.seriesOf(note);
    return [
      {
        to: series.contract,
        data: encodeFunctionData({
          abi: MNOTE_ABI,
          functionName: "transferNote",
          args: [note.id, to as Hex, note.version, amount === undefined ? note.units : toUnits(amount, series, note)],
        }),
      },
    ];
  }

  redeemCalls(note: NoteView, to: string, amount?: number): Call[] {
    const series = this.seriesOf(note);
    return [
      {
        to: series.contract,
        data: encodeFunctionData({
          abi: MNOTE_ABI,
          functionName: "redeemNote",
          args: [note.id, to as Hex, note.version, amount === undefined ? note.units : toUnits(amount, series, note)],
        }),
      },
    ];
  }
}

/** The token's units (6 dp) → dollars with cents, exactly: the cents are counted in integers first. */
export function dollarsOf(units: bigint): number {
  return Number(units / 10_000n) / 100;
}

/**
 * Dollars (up to two decimals) → the contract's units. Refuses, before anything
 * is signed, what the contract would refuse with BadAmount: less than a cent, a
 * fraction of a cent, cents on a whole-dollar series (A), or more than the note.
 */
export function toUnits(dollars: number, series: Series, of?: NoteView): bigint {
  const cents = Math.round(dollars * 100);
  if (!Number.isFinite(dollars) || Math.abs(dollars * 100 - cents) > 1e-6 || cents < 1) throw new Error("An amount is dollars and cents.");
  const units = BigInt(cents) * 10_000n;
  if (units % series.unit !== 0n) {
    throw new Error(series.unit === 1_000_000n ? "A note in this series is a whole number of dollars." : "That amount doesn't fit this series.");
  }
  if (of && units > of.units) throw new Error("That is more than the note is worth.");
  return units;
}
